import { array as yupArray } from 'yup'

import { type TagValidationResult } from 'components/common/AddTagsForm'
import { naturalSortBy } from 'utils/list'

import type {
  MultiSelectTags,
  UserDefinedService
} from 'types/api/services/service'

export function getTagLabel(tagKey: string, tagValue: string) {
  return `${tagKey}: ${tagValue}`
}

// todo: consolidate PRODUCTION_TAG_OPTION_TEXT and PRODUCTION_TAG_OPTION_LABEL
export const PRODUCTION_TAG_OPTION_TEXT = getTagLabel(
  'environment',
  'production'
)

export const PRODUCTION_TAG_OPTION_LABEL = getTagLabel(
  'environment',
  'production'
)

export function getHasProductionEnvironmentTag(service: UserDefinedService) {
  return service?.multiSelectTags?.environment?.includes('production') ?? false
}

// todo: deprecate service.tags
export function isProductionService(service: UserDefinedService) {
  return (
    service?.tags?.environment === 'production' ||
    getHasProductionEnvironmentTag(service)
  )
}

export function getIsProductionServiceTag(option: string) {
  return option === PRODUCTION_TAG_OPTION_TEXT
}

export function getCleanedServiceTags(tags: MultiSelectTags) {
  const cleanedTags = Object.entries(tags).reduce(
    (cleanedTags: MultiSelectTags, [tagName, tagValues]) => {
      /* clean up any null values from previous saves */
      const cleanedValues = tagValues.filter(Boolean)

      if (cleanedValues.length > 0) {
        cleanedTags[tagName] = cleanedValues
      }

      return cleanedTags
    },
    {}
  )

  return Object.keys(cleanedTags).length > 0 ? cleanedTags : null
}

export function getServiceTagsAsArray(tags: MultiSelectTags) {
  return Object.entries(tags).flatMap(([tagKey, tagValues]) => {
    return tagValues.flatMap((tagValue) => getTagLabel(tagKey, tagValue))
  })
}

/**
 * Tags are sorted alphabetically with production tag first.
 */
export function sortTagsWithProductionTagFirst(tags: string[]) {
  return naturalSortBy(tags, [
    (tag: string) => !getIsProductionServiceTag(tag),
    (tag: string) => tag
  ])
}

/**
 * Get display tags and overflow tags.
 * Tags are sorted according to `sortTagsWithProductionTagFirst`
 */
export function getTagsForDisplay(
  multiSelectTags: MultiSelectTags = {},
  tagsDisplayCount = 1
) {
  const serviceTagsCleaned = getServiceTagsAsArray(
    getCleanedServiceTags(multiSelectTags) ?? {}
  )

  const serviceTags = sortTagsWithProductionTagFirst(serviceTagsCleaned)

  return {
    displayTags: serviceTags.slice(0, tagsDisplayCount),
    overflowTags: serviceTags.slice(tagsDisplayCount)
  }
}

/* ServiceSettingsTags utils */

/**
 * This should fulfill backend service validation
 * libraries/core/src/main/java/com/gremlininc/customerservices/datamodels/impact/ValidateMultiSelectTags.java
 */
const ALLOWED_BEGIN_END_CHARS_REGEX = /^[a-zA-Z0-9]+$/
const ALLOWED_MIDDLE_CHARS_REGEX = /^[a-zA-Z0-9-_.]+$/

export const ERROR_MAP_TAG_NAME = {
  Exists: 'Tag with that name is already saved',
  Invalid:
    'Invalid characters. Must begin and end with an alphanumeric character ([a-z0-9A-Z]) with dashes (-), underscores (_), dots (.), and alphanumerics',
  Required: 'Tag Name is required'
} as const

export const ERROR_MAP_TAG_VALUE = {
  Invalid: ERROR_MAP_TAG_NAME.Invalid,
  Required: 'At least one tag value is required'
} as const

function getHasOnlyAllowedCharacters(str: string) {
  const char1 = str[0]
  const charLast = str.at(-1)

  if (str.length > 2) {
    const stringStartAndEndValid = [char1, charLast].every((char: string) =>
      ALLOWED_BEGIN_END_CHARS_REGEX.test(char)
    )

    const innerChars = str.slice(1, -1)
    const stringInnerValid = ALLOWED_MIDDLE_CHARS_REGEX.test(innerChars)

    return stringStartAndEndValid && stringInnerValid
  }

  return ALLOWED_BEGIN_END_CHARS_REGEX.test(str)
}

export function validateServiceTagName(
  tagName: string,
  tagNames: string[]
): TagValidationResult {
  const errors = new Set<string>()

  if (tagNames.includes(tagName)) {
    errors.add(ERROR_MAP_TAG_NAME.Exists)
  }

  if (!tagName || tagName.length === 0) {
    errors.add(ERROR_MAP_TAG_NAME.Required)
  } else if (!getHasOnlyAllowedCharacters(tagName)) {
    errors.add(ERROR_MAP_TAG_NAME.Invalid)
  }

  return {
    isValid: errors.size === 0,
    errors: errors.size > 0 ? [...errors] : null
  }
}

export function validateServiceTagValues(
  tagValues: string[]
): TagValidationResult {
  const errors = new Set<string>()

  if (!yupArray().min(1).required().isValidSync(tagValues)) {
    errors.add(ERROR_MAP_TAG_VALUE.Required)
  }

  tagValues.forEach((tagValue) => {
    if (!getHasOnlyAllowedCharacters(tagValue)) {
      errors.add(ERROR_MAP_TAG_VALUE.Invalid)
    }
  })

  return {
    isValid: errors.size === 0,
    errors: errors.size > 0 ? [...errors] : null
  }
}
